import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import CalendarSelectionDrawer from "../CalendarSelectionDrawer";
import { CalendarSelectionDrawerProps } from "../CalendarSelectionDrawer/CalendarSelectionDrawer";
import { useToggle } from "@pushpress/shared-components";
import { postConnectToGoogle } from "../../../apis/googleSyncApi";
import { AppContext } from "../../../context/app";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { useSyncGoogleCalendarMutation } from "../CalendarSync-generated.hooks";

// internal hook
function useProvider() {
  const { client } = useContext(AppContext);

  // open the staff calendar list drawer

  const [calendarSelectionDrawerProps, setCalendarSelectionDrawerProps] =
    useState<CalendarSelectionDrawerProps>({ visible: false });

  const showStaffCalendars = useCallback(
    (userUuid: string, { onSuccess }: { onSuccess: VoidFunction }) => {
      setCalendarSelectionDrawerProps({
        staffId: userUuid,
        visible: true,
        onSuccess: () => {
          setCalendarSelectionDrawerProps({ visible: false });
          onSuccess();
        },
        onClose: () => {
          setCalendarSelectionDrawerProps({ visible: false });
        },
      });
    },
    [],
  );

  // sync google calendar

  const [googleCalendarSyncRequested, googleCalendarSyncRequestedToggle] =
    useToggle(false);

  const [syncGoogleCalendarMutate, { loading: syncingGoogleCalendar }] =
    useSyncGoogleCalendarMutation();

  const syncGoogleCalendar = useCallback(
    async (
      userUuid: string,
      { onSuccess }: { onSuccess?: VoidFunction } = {},
    ) => {
      try {
        await syncGoogleCalendarMutate({
          variables: {
            userUuid,
          },
        });
        toast.success("Sync Started. Full Sync can take up to 10 minutes.", {
          ...defaultToastOptions,
          autoClose: 10000,
        });
        googleCalendarSyncRequestedToggle.on();
        onSuccess?.();
      } catch (error) {
        toast.error(extractError(error), defaultToastOptions);
      }
    },
    [syncGoogleCalendarMutate, googleCalendarSyncRequestedToggle],
  );

  // connects a google account to a user

  const [isConnectingGoogle, connectGoogleToggle] = useToggle(false);

  const connectGoogleAccount = useCallback(
    async (userUuid: string) => {
      connectGoogleToggle.on();
      try {
        const redirectUrl = location?.search?.includes("delayStatusCheck=1")
          ? location.href
          : `${location.href}?delayStatusCheck=1`;
        const response = await postConnectToGoogle({
          clientId: client?.uuid,
          userId: userUuid,
          gymName: client.company || "",
          redirectUrl,
          timezone: client.timezone,
        });
        if (response.authorizedUrl) {
          toast.success(
            "You will be redirected to finish your google connection!",
            defaultToastOptions,
          );
          location.assign(response.authorizedUrl);
        } else {
          throw new Error("Failed to obtain google oauth url.");
        }
      } catch (err) {
        console.error("Failed...", err);
        toast(err, defaultToastOptions);
      } finally {
        connectGoogleToggle.off();
      }
    },
    [connectGoogleToggle, client],
  );

  const contextValue = useMemo(() => {
    return {
      showStaffCalendars,
      isConnectingGoogle,
      connectGoogleAccount,
      syncGoogleCalendar,
      syncingGoogleCalendar,
      googleCalendarSyncRequested,
    };
  }, [
    showStaffCalendars,
    isConnectingGoogle,
    connectGoogleAccount,
    syncGoogleCalendar,
    syncingGoogleCalendar,
    googleCalendarSyncRequested,
  ]);

  return { contextValue, calendarSelectionDrawerProps };
}

// Create the context
const CalendarSyncContext = createContext<
  ReturnType<typeof useProvider>["contextValue"] | undefined
>(undefined);

// Create the provider component
const CalendarSyncProvider = ({ children }: { children?: ReactNode }) => {
  const { contextValue, calendarSelectionDrawerProps } = useProvider();
  return (
    <CalendarSyncContext.Provider value={contextValue}>
      {children}
      <CalendarSelectionDrawer {...calendarSelectionDrawerProps} />
    </CalendarSyncContext.Provider>
  );
};

// Create a hook to use the context
export function useCalendarSync() {
  const context = useContext(CalendarSyncContext);
  if (!context) {
    throw new Error(
      "useCalendarSync must be used within a CalendarSyncProvider",
    );
  }
  return context;
}

export default CalendarSyncProvider;
